import {
  IonButtons,
  IonToolbar,
  useIonAlert,
  useIonPopover,
  IonIcon,
} from '@ionic/react';
import { batteryFull, batteryHalf, batteryDead } from 'ionicons/icons';
import { useFirmwareUpdate } from 'components/useUpdateOverlay';
import { User } from 'models/User';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthService } from '../../../services/Authentication';
import OptionsPopOver from './OptionsPopOver';
import DynamicButton from 'components/buttons/DynamicButton';
import Trans from 'util/Trans';


interface ToolbarProps {
  currentUser: User;
  bleConnected: boolean;
  openDeviceModal: () => void;
  openDebugModal: () => void;
  batteryLevel?: number;
}

const Toolbar: FunctionComponent<ToolbarProps> = ({
  bleConnected,
  currentUser,
  openDeviceModal,
  openDebugModal,
  batteryLevel,
}) => {
  const { t } = useTranslation();
  const AuthService = useAuthService();
  const [presentAlert] = useIonAlert();
  const { checkForUpdates } = useFirmwareUpdate();

  // This function is called when the user confirmed the username change in the changeUserNameAlert.
  // The username is only changed if the user entered a new one, and it is not empty.
  const changeUserName = async ({ displayName }: { displayName: string }) => {
    if (displayName.length > 0 && displayName !== currentUser.displayName)
      currentUser.changeDisplayName(displayName);
  };

  // This function is called when the user clicks the "Change Username" button in the menu.
  // It opens an alert with an input field for the new username.
  const openUserNameAlert = () => {
    presentAlert({
      header: 'Change Username',
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        { text: 'OK', handler: changeUserName },
      ],
      inputs: [
        {
          type: 'text',
          name: 'displayName',
          value: currentUser.displayName,
        },
      ],
    });
  };

  // This hook creates a popover for the menu.
  const [_showOptions, dismiss] = useIonPopover(OptionsPopOver, {
    changeUsername: openUserNameAlert,
    onHide: () => dismiss(),
    checkUpdates: bleConnected
      ? () => checkForUpdates({ force: true })
      : undefined,
    logOut: AuthService.signOut.bind(AuthService),
  });

  return (
    <IonToolbar class="notchSafe" style={{ position: 'relative' }}>
      <IonButtons slot="primary">
        <DynamicButton
          iconName="FreakConnectUpIcon"
          text={<Trans i18nKey="Weiter" />}
          buttonType="icon-full"
          fill="clear"
          size="xlarge"
          onClick={openDeviceModal}
        />
      </IonButtons>
      {/* Batterieanzeige mittig im Toolbar */}
      {bleConnected && typeof batteryLevel === 'number' && !isNaN(batteryLevel) && (
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            alignItems: 'center',
            fontWeight: 700,
            background: 'rgba(0,0,0,0.25)',
            padding: '4px 14px',
            minWidth: 0,
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            borderRadius: 18,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          <svg
            width="28"
            height="28"
            viewBox="0 0 48 48"
            style={{ marginRight: 8 }}
          >
            <rect x="8" y="14" width="32" height="20" rx="6" fill="#222" stroke="#fff" strokeWidth="2" />
            <rect x="40" y="20" width="4" height="8" rx="2" fill="#fff" />
            <rect
              x="10"
              y="16"
              width={(batteryLevel > 100 ? 100 : batteryLevel < 0 ? 0 : batteryLevel) * 0.28}
              height="16"
              rx="4"
              fill={
                batteryLevel < 20
                  ? '#ff3b30'
                  : batteryLevel < 60
                  ? '#ffd600'
                  : '#4cd964'
              }
            />
          </svg>
          <span style={{ color: '#fff', fontSize: 20, fontWeight: 800, textShadow: '0 2px 8px #000' }}>{batteryLevel}%</span>
        </div>
      )}
    </IonToolbar>
  );
};

export default Toolbar;
